'use client';

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useI18n } from '@fshr/context/i18n-context';
import { cn } from '@fshr/shared-react';
import { useMediaQuery } from 'react-responsive';
import BevSafeText from '../bev-safe-text';
import Button from '../button';
import IconCheckmarkSimple from '../icon/checkmark_simple';
import { getStepStatus, isReviewStep, type StepStatus } from './types';

type StepperNavProps = {
	steps: { id: string; stepTitle: string }[];
	currentStep: number;
	completedSteps: ReadonlySet<number>;
	onStepSelect: (index: number) => void;
};

const MOBILE_HEADER_FALLBACK_PX = 60;

function MobileFixedNav({ children, currentStep }: { children: ReactNode; currentStep: number }) {
	const { t } = useI18n();
	const spacerRef = useRef<HTMLDivElement>(null);
	const navRef = useRef<HTMLElement>(null);
	const isFixedRef = useRef(false);
	const [isFixed, setIsFixed] = useState(false);
	const [topOffset, setTopOffset] = useState(MOBILE_HEADER_FALLBACK_PX);
	const [navHeight, setNavHeight] = useState(0);

	useLayoutEffect(() => {
		const measure = () => {
			const header = document.getElementById('header-nav');
			const top = header
				? Math.round(header.getBoundingClientRect().bottom)
				: MOBILE_HEADER_FALLBACK_PX;
			setTopOffset(top);

			const spacer = spacerRef.current;
			const nav = navRef.current;
			if (!spacer || !nav) return;

			const height = nav.offsetHeight;
			const spacerRect = spacer.getBoundingClientRect();
			const nextFixed = spacerRect.top <= top;

			setNavHeight(height);

			if (nextFixed !== isFixedRef.current) {
				isFixedRef.current = nextFixed;
				setIsFixed(nextFixed);
			}
		};

		measure();
		window.addEventListener('scroll', measure, { passive: true, capture: true });
		window.addEventListener('resize', measure);

		const resizeObserver =
			typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
		const header = document.getElementById('header-nav');
		if (header) resizeObserver?.observe(header);
		if (spacerRef.current) resizeObserver?.observe(spacerRef.current);
		if (navRef.current) resizeObserver?.observe(navRef.current);

		return () => {
			window.removeEventListener('scroll', measure, true);
			window.removeEventListener('resize', measure);
			resizeObserver?.disconnect();
		};
	}, []);

	useLayoutEffect(() => {
		const nav = navRef.current;
		if (!nav) return;
		const activeStep = nav.querySelector<HTMLElement>(`[data-step-index="${currentStep}"]`);
		activeStep?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
	}, [currentStep]);

	return (
		<div ref={spacerRef} className="w-full" style={isFixed ? { height: navHeight } : undefined}>
			<nav
				ref={navRef}
				aria-label={t('Form steps')}
				className={cn(
					'flex w-full flex-row items-start overflow-x-auto bg-surface [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
					{
						'z-40 left-0 w-full pt-[15px] pb-[10px] px-[15px] md:pt-[20px] md:pb-[15px] md:px-[60px] shadow-[0px_5px_15px_0px_#0303031F]':
							isFixed,
					}
				)}
				style={
					isFixed
						? {
								position: 'fixed',
								top: topOffset,
							}
						: undefined
				}
			>
				{children}
			</nav>
		</div>
	);
}

export const StepperNav = ({
	steps,
	currentStep,
	completedSteps,
	onStepSelect,
}: StepperNavProps) => {
	const { t } = useI18n();
	const isDesktop = useMediaQuery({ minWidth: 960 });

	if (isDesktop) {
		return (
			<nav
				aria-label={t('Form steps')}
				className="flex w-full flex-col items-start spacing-stack-xs"
			>
				{steps.map(({ id, stepTitle }, index) => {
					const status = getStepStatus(index, currentStep, completedSteps);
					const isLast = index === steps.length - 1;

					return (
						<div key={id} className="flex w-full flex-col items-start spacing-stack-xs">
							<DesktopStep
								index={index}
								label={stepTitle}
								status={status}
								onSelect={() => onStepSelect(index)}
							/>
							{!isLast ? <DesktopStepDivider /> : null}
						</div>
					);
				})}
			</nav>
		);
	}

	return (
		<MobileFixedNav currentStep={currentStep}>
			{steps.map(({ id, stepTitle }, index) => {
				const status = getStepStatus(index, currentStep, completedSteps);
				const nextStepStatus = getStepStatus(index + 1, currentStep, completedSteps);
				const label = isReviewStep(stepTitle) ? stepTitle : t('Step {0}', [index + 1]);

				return (
					<MobileStep
						key={id}
						index={index}
						label={label}
						status={status}
						nextStepStatus={nextStepStatus}
						onSelect={() => onStepSelect(index)}
					/>
				);
			})}
		</MobileFixedNav>
	);
};

type DesktopStepProps = {
	index: number;
	label: string;
	status: StepStatus;
	onSelect: () => void;
};

const DesktopStep = ({ index, label, status, onSelect }: DesktopStepProps) => {
	const isCurrent = status === 'current';
	const isCompleted = status === 'completed';
	const isClickable = isCompleted;

	return (
		<Button
			type="button"
			intent="unstyled"
			onClick={onSelect}
			disabled={!isClickable && !isCurrent}
			aria-current={isCurrent ? 'step' : undefined}
			className={cn('group flex w-full flex-row items-center spacing-inline-s text-left', {
				'cursor-not-allowed': status === 'upcoming',
				'cursor-pointer': isClickable,
				'cursor-default': isCurrent && !isClickable,
			})}
		>
			<span
				className={cn('flex size-8 shrink-0 items-center justify-center rounded-full', {
					'bg-text-primary': isCurrent,
					'bg-[#989898] group-hover:bg-text-primary': isCompleted,
					'border border-text-disabled bg-surface': status === 'upcoming',
				})}
			>
				{isCompleted ? (
					<span className="[&_path]:fill-white">
						<IconCheckmarkSimple width={12} height={12} />
					</span>
				) : (
					<span className="text-subtitle2 translate-y-[0.2em]">
						<span
							className={cn({
								'text-text-inverse-primary': isCurrent,
								'text-text-disabled': status === 'upcoming',
							})}
						>
							{index + 1}
						</span>
					</span>
				)}
			</span>
			<span className="text-label">
				<span
					className={cn({
						'text-text-primary': isCurrent,
						'text-text-disabled group-hover:text-text-primary': isCompleted,
						'text-text-disabled': status === 'upcoming',
					})}
				>
					<BevSafeText>{label}</BevSafeText>
				</span>
			</span>
		</Button>
	);
};

const DesktopStepDivider = () => (
	<div className="flex w-8 justify-center" aria-hidden>
		<div className="h-5 w-px bg-border-subtle" />
	</div>
);

type MobileStepProps = {
	index: number;
	label: string;
	status: StepStatus;
	nextStepStatus: StepStatus;
	onSelect: () => void;
};

const MobileStep = ({ index, label, status, nextStepStatus, onSelect }: MobileStepProps) => {
	const isCurrent = status === 'current';
	const isCompleted = status === 'completed';
	const isUpcoming = status === 'upcoming';
	const isNextUpcoming = nextStepStatus === 'upcoming';
	const isClickable = isCompleted;
	const completeLineClass = 'bg-text-primary';
	const incompleteLineClass = 'bg-border-subtle';

	const leftLineClass = isCurrent || isCompleted ? completeLineClass : incompleteLineClass;
	const rightLineClass = isUpcoming || isNextUpcoming ? incompleteLineClass : completeLineClass;

	return (
		<Button
			type="button"
			intent="unstyled"
			onClick={onSelect}
			disabled={!isClickable && !isCurrent}
			aria-current={isCurrent ? 'step' : undefined}
			data-step-index={index}
			className={cn('group flex min-w-[100px] flex-1 flex-col items-center gap-2', {
				'cursor-not-allowed': status === 'upcoming',
				'cursor-pointer': isClickable,
				'cursor-default': isCurrent && !isClickable,
			})}
		>
			<div className="flex h-2.5 w-full items-center gap-[3px]">
				<span className={cn('h-px min-h-px flex-1', leftLineClass)} />
				{isCompleted ? (
					<span className="inline-flex size-2.5 shrink-0 items-center justify-center [&_path]:fill-[var(--color-text-primary)]">
						<IconCheckmarkSimple width={10} height={10} />
					</span>
				) : (
					<span
						className={cn('inline-flex size-2.5 shrink-0 rounded-full', {
							'bg-text-primary': isCurrent,
							'border border-border-subtle bg-surface': status === 'upcoming',
						})}
					/>
				)}
				<span className={cn('h-px min-h-px flex-1', rightLineClass)} />
			</div>
			<div className="text-button2">
				<div
					className={cn('text-center', {
						'text-text-primary': isCurrent,
						'text-text-disabled group-hover:text-text-primary': isCompleted,
						'text-text-disabled': status === 'upcoming',
					})}
				>
					<BevSafeText>{label}</BevSafeText>
				</div>
			</div>
		</Button>
	);
};
